import type { IPublicClientApplication } from '@azure/msal-browser';
import { environment } from '@/config/environment';

/**
 * Token Refresh Service
 * Provides proactive token refresh functionality to prevent 401 errors
 * Refreshes tokens before they expire (default: every 50 minutes for 1-hour token expiry)
 */
export class TokenRefreshService {
  private refreshInterval: NodeJS.Timeout | null = null;
  private msalInstance: IPublicClientApplication | null = null;
  private readonly refreshIntervalMs: number;

  /**
   * @param refreshIntervalMinutes - Minutes between token refresh attempts (default: 50)
   * Azure AD default token expiry is 60 minutes, so 50 minutes ensures refresh before expiry
   */
  constructor(refreshIntervalMinutes: number = 50) {
    this.refreshIntervalMs = refreshIntervalMinutes * 60 * 1000;
  }

  /**
   * Start the proactive token refresh service
   * @param msalInstance - The MSAL instance to use for token refresh
   */
  start(msalInstance: IPublicClientApplication): void {
    // Always update the instance reference (handles HMR case where instance may change)
    this.msalInstance = msalInstance;

    if (this.refreshInterval) {
      console.log('[TokenRefreshService] Service already running, updated MSAL instance reference');
      return;
    }

    console.log(
      `[TokenRefreshService] Starting proactive token refresh (interval: ${this.refreshIntervalMs / 60000} minutes)`
    );

    // Perform initial refresh
    this.refreshToken();

    // Set up recurring refresh
    this.refreshInterval = setInterval(() => {
      this.refreshToken();
    }, this.refreshIntervalMs);
  }

  /**
   * Stop the proactive token refresh service
   */
  stop(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      console.log('[TokenRefreshService] Stopped proactive token refresh');
    }
  }

  /**
   * Manually trigger a token refresh
   */
  async refreshToken(): Promise<boolean> {
    if (!this.msalInstance) {
      console.warn('[TokenRefreshService] MSAL instance not available');
      return false;
    }

    const account = this.msalInstance.getActiveAccount();

    if (!account) {
      console.warn('[TokenRefreshService] No active account found, skipping token refresh');
      return false;
    }

    try {
      const tokenRequest = {
        scopes: [environment.msalConfig.apiScope],
        account: account,
      };

      const response = await this.msalInstance.acquireTokenSilent(tokenRequest);

      if (response?.accessToken) {
        console.log('[TokenRefreshService] Token refreshed successfully');
        return true;
      }

      console.warn('[TokenRefreshService] Token refresh returned no access token');
      return false;
    } catch (error) {
      const errorName = (error as { name?: string })?.name;
      const errorMessage = (error as { message?: string })?.message || '';

      // Don't log errors for interaction required - these are expected when refresh token expires
      if (
        errorName === 'InteractionRequiredAuthError' ||
        errorMessage.includes('interaction_required') ||
        errorMessage.includes('consent_required') ||
        errorMessage.includes('login_required')
      ) {
        console.log(
          '[TokenRefreshService] Interaction required for token refresh - user will be prompted on next API call'
        );
      } else {
        console.warn('[TokenRefreshService] Token refresh failed:', errorName, errorMessage);
      }

      return false;
    }
  }

  /**
   * Get the current refresh interval in milliseconds
   */
  getRefreshInterval(): number {
    return this.refreshIntervalMs;
  }

  /**
   * Check if the service is currently running
   */
  isRunning(): boolean {
    return this.refreshInterval !== null;
  }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService(50);
